import { useEffect, useState } from 'react'
import QRCode from 'qrcode'

/**
 * QR ที่สร้างในเบราว์เซอร์ — ไม่ยิงไปเซิร์ฟเวอร์ไหนทั้งนั้น
 *
 * ทำไมสร้างเองแทนที่จะดึงรูป QR จาก LINE:
 *  - LINE ไม่มี API ที่ให้รูป QR อย่างเป็นทางการ (มีแต่ให้ดาวน์โหลดในหน้า OA Manager)
 *    URL รูปที่คนชอบเอามาแปะกันเป็นของที่ไม่มีสัญญา วันหนึ่งเปลี่ยนแล้วรูปหายเงียบ
 *  - QR คือการ "เข้ารหัสข้อความ" ล้วน ๆ ไม่ใช่ข้อมูลลับที่ต้องไปขอใคร
 *    ตราบใดที่ addFriendUrl ถูก QR ก็ถูก ตรวจได้ด้วยตาจากข้อความใต้รูป
 *
 * สร้างที่ฝั่งหน้าจอไม่ใช่ backend เพราะ frontend มี npm ci ใน CI อยู่แล้ว
 * ส่วน backend ลง Plesk ผ่าน FTP — เพิ่ม dependency ทีต้องไปกด NPM install เองในหน้า
 * Plesk ก่อน (deploy-api.yml หยุด pipeline ให้ด้วยเจตนา) ราคาต่างกันมาก
 */

/** ระดับกู้คืนข้อมูล 'M' = ทน QR เสียหาย/โดนบัง ~15% พอสำหรับโปสเตอร์ที่อาจมีรอยพับ */
const LEVEL = 'M' as const

/** ขนาดไฟล์ PNG ตอนดาวน์โหลด — ใหญ่พอเอาไปพิมพ์ A4 ได้โดยไม่แตก */
const PNG_SIZE = 1024

type Props = {
  /** ข้อความที่จะเข้ารหัส — ปกติคือ https://line.me/R/ti/p/@xxxx */
  value: string
  /** ขนาดที่แสดงบนหน้าจอ (px) */
  size?: number
  /** ชื่อไฟล์ตอนกดดาวน์โหลด ไม่ต้องใส่นามสกุล */
  filename?: string
}

export function QrCode({ value, size = 200, filename = 'line-qr' }: Props) {
  const [svg, setSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setError(null)
    QRCode.toString(value, { type: 'svg', errorCorrectionLevel: LEVEL, margin: 1 })
      .then((out) => {
        if (!alive) return
        /* ถอด width/height ที่ library ใส่มาออก เหลือแต่ viewBox ให้ CSS คุมขนาดเอง
           ไม่งั้นรูปจะไม่ยืดตามกรอบและล้นบนจอแคบ */
        setSvg(out.replace(/\s(width|height)="[^"]*"/g, ''))
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : 'สร้าง QR ไม่สำเร็จ')
      })
    return () => {
      alive = false
    }
  }, [value])

  const download = async (kind: 'png' | 'svg') => {
    try {
      const href =
        kind === 'png'
          ? await QRCode.toDataURL(value, {
              errorCorrectionLevel: LEVEL,
              width: PNG_SIZE,
              margin: 2,
            })
          : // ใช้ตัวที่ยังมี width/height ติดมา — ไฟล์ที่เอาไปเปิดในโปรแกรมอื่นต้องมีขนาด
            `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
              await QRCode.toString(value, { type: 'svg', errorCorrectionLevel: LEVEL, margin: 2 }),
            )}`

      const a = document.createElement('a')
      a.href = href
      a.download = `${filename}.${kind}`
      a.click()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ดาวน์โหลดไม่สำเร็จ')
    }
  }

  if (error) return <p className="error">สร้าง QR ไม่ได้: {error}</p>

  return (
    <div className="qr">
      <div
        className="qr__img"
        style={{ width: size, height: size }}
        /* svg มาจาก qrcode ในเครื่องเรา ไม่ใช่จากผู้ใช้หรือเน็ตเวิร์ก — ไม่มีทางมี script
           ปนมา (ค่า value ถูก encode เป็นโมดูลขาวดำ ไม่ได้ถูกเขียนลง markup) */
        dangerouslySetInnerHTML={{ __html: svg ?? '' }}
        aria-label={`QR สำหรับ ${value}`}
        role="img"
      />
      <div className="qr__actions">
        <button type="button" className="ghost" onClick={() => void download('png')}>
          PNG
        </button>
        <button type="button" className="ghost" onClick={() => void download('svg')}>
          SVG
        </button>
      </div>
    </div>
  )
}
